import classnames from "classnames";
import { format } from "date-fns";
import qs from "qs";
import * as React from "react";

import {
    Input,
    ISearchFacetConfig,
    Page,
    PageHeaderActions,
    PageSection,
    Paginator,
    RouteLink,
    SearchContainer,
    SelectInput,
    TableColumn,
    Text,
} from "../../../";
import { EnvelopeIcon, EnvelopeOpenIcon } from "../../../assets/icons";
import { useMessagingStyles } from "../styles";
import {
    Message,
    MessageBoxOptionsEnum,
    MessageSearchCriteria,
} from "../types";
import { useMessageSearch } from "./_infra/messageListingProvider";

export const MessageListingDesktop = () => {
    const { state, actions } = useMessageSearch();
    const { searchCriteria } = state;
    const [queryString, setQueryString] = React.useState("");
    const [currentSearchText, setCurrentSearchText] = React.useState<string>(
        ""
    );
    const [selectedBox, setSelectedBox] = React.useState<MessageBoxOptionsEnum>(
        MessageBoxOptionsEnum.Inbox
    );
    const classes = useMessagingStyles({});
    const baseId = "messageListing";

    React.useEffect(() => {
        setQueryString(
            qs.stringify(searchCriteria, {
                skipNulls: true,
                sort: (a, b) => a.localeCompare(b),
            })
        );
        setCurrentSearchText(searchCriteria.searchText || "");
        setSelectedBox(
            searchCriteria.box
                ? MessageBoxOptionsEnum.fromName(searchCriteria.box)
                : MessageBoxOptionsEnum.Inbox
        );
    }, [searchCriteria]);

    const boxOptions = React.useMemo(
        () =>
            MessageBoxOptionsEnum.asArray().filter(
                el =>
                    !(
                        appConfig.appName !== "admin" &&
                        el.name === MessageBoxOptionsEnum.AllMessages.name
                    )
            ),
        []
    );

    const rowClasses = (isRead: boolean) =>
        classnames({
            [classes.unread]:
                !isRead &&
                searchCriteria.box !== MessageBoxOptionsEnum.AllMessages.name,
        });

    const renderRecipients = (message: Message) => (
        <>
            {message.firstRecipient ? message.firstRecipient.displayName : null}
            {message.recipients && message.recipients.length > 1
                ? ` and ${message.recipients.length - 1} other${
                      message.recipients.length === 2 ? "" : "s"
                  }`
                : null}
        </>
    );

    const MessageLink = (props: {
        messageId: number;
        isRead: boolean;
        children: React.ReactNode;
    }) => (
        <RouteLink
            data-cy="messageLink"
            highlightType="internal"
            to={{
                pathname: `/messages/${props.messageId}`,
                state: {
                    messageIds: state.searchResults.messageIds,
                    queryString,
                },
            }}
        >
            <Text
                variant="body1"
                className={rowClasses(props.isRead)}
                component="div"
            >
                {props.children}
            </Text>
        </RouteLink>
    );

    const columns: TableColumn[] = [
        {
            title: "id",
            field: "messageId",
            hidden: true,
        },
        {
            title: `${
                searchCriteria.box === MessageBoxOptionsEnum.Sent.name
                    ? "to"
                    : "from"
            }`,
            field: `${
                searchCriteria.box === MessageBoxOptionsEnum.Sent.name
                    ? "firstRecipient"
                    : "sender"
            }`,
            hidden: false,
            truncateText: true,
            sortable: true,
            columnSize:
                searchCriteria.box === MessageBoxOptionsEnum.AllMessages.name
                    ? "medium"
                    : "larger",
            render: (row: Message) => (
                <MessageLink messageId={row.messageId} isRead={row.isRead}>
                    {searchCriteria.box === MessageBoxOptionsEnum.Sent.name ? (
                        renderRecipients(row)
                    ) : (
                        <>{row.sender ? row.sender.displayName : null}</>
                    )}
                </MessageLink>
            ),
        },
        {
            title: "to",
            field: "firstRecipient",
            hidden:
                searchCriteria.box !== MessageBoxOptionsEnum.AllMessages.name,
            truncateText: true,
            sortable: true,
            columnSize: "larger",
            render: (row: Message) => (
                <MessageLink messageId={row.messageId} isRead={row.isRead}>
                    {renderRecipients(row)}
                </MessageLink>
            ),
        },
        {
            title: "subject",
            field: "subject",
            sortable: true,
            columnSize: "largest",
            truncateText: true,
            render: (row: Message) => (
                <span className={rowClasses(row.isRead)}>{row.subject}</span>
            ),
        },
        {
            title: "date",
            field: "createTimestamp",
            sortable: true,
            align: "right",
            render: (row: Message) => (
                <span className={rowClasses(row.isRead)}>
                    {format(row.createTimestamp, "P p")}
                </span>
            ),
        },
    ];

    const handleFilter = (fId: string, fName: string) => (catItem?: {
        label: string;
        value: string;
    }) => {
        actions.filterMessages({
            id: fId,
            name: fName,
            item: catItem,
        });
    };

    const facetConfig: ISearchFacetConfig = {
        handleSingleSelection: handleFilter,
        facets: [
            {
                id: "status",
                title: "message status",
                idPropName: "messageStatusId",
                namePropName: "messageStatusName",
                isMultiple: false,
            },
            {
                id: "fromRole",
                title: "from",
                idPropName: "fromRoleId",
                namePropName: "fromRoleName",
                isMultiple: false,
            },
            {
                id: "toRole",
                title: "to",
                idPropName: "toRoleId",
                namePropName: "toRoleName",
                isMultiple: false,
            },
            // These are "fake" facets; they are used only the display the query params values to refined by section;
            // if those filters are removed then these facets will not be displayed since they are not returned by API endpoint
            {
                id: "contextJob",
                title: "contextJob",
                idPropName: "contextJobId",
                namePropName: "contextJobName",
                isMultiple: false,
            },
            {
                id: "contextUser",
                title: "contextUser",
                idPropName: "contextUserId",
                namePropName: "contextUserName",
                isMultiple: false,
            },
        ],
    };

    const handleRequestSort = (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        property: string
    ) => {
        actions.sort(property);
    };

    const handleTextSearchEvent = (event: React.ChangeEvent<any>) => {
        setCurrentSearchText(event.target.value);
    };

    const handleTextSearchKeyPress = event => {
        if (event.key === "Enter") {
            actions.setSearchCriteria({
                ...searchCriteria,
                searchText: currentSearchText,
            });
        }
    };

    const handleSearch = async () => {
        const sortField =
            searchCriteria.searchText !== currentSearchText
                ? "rank"
                : searchCriteria.sortField;
        const sortDir =
            searchCriteria.searchText !== currentSearchText
                ? "desc"
                : searchCriteria.sortDir;

        const newCriteria: MessageSearchCriteria = {
            ...searchCriteria,
            box: selectedBox.name,
            searchText: currentSearchText,
            sortField,
            sortDir,
        };

        await actions.setSearchCriteria(newCriteria);
    };

    const handleSelectedAction = React.useCallback(
        async (action: "read" | "unread") => {
            switch (action) {
                case "read":
                    await actions.markSelectedRead();
                    break;

                case "unread":
                    await actions.markSelectedUnread();
                    break;
            }
        },
        [actions]
    );

    const handleBoxFilter = (event: never, value: MessageBoxOptionsEnum) => {
        setSelectedBox(value);
    };

    const changePage = (direction: "prev" | "next") => {
        actions.navigate(direction);
    };

    const renderRight = (): React.ReactNode => {
        const { totalResults } = state.searchResults;
        const { pageSize, pageIndex } = searchCriteria;

        const start = (pageIndex - 1) * pageSize + 1;
        const remaining =
            totalResults < pageSize ? totalResults : totalResults - start;
        const maxOnPage =
            remaining < pageSize ? totalResults : pageIndex * pageSize;
        return (
            !!state.searchResults &&
            totalResults > 0 && (
                <Paginator
                    baseId="searchMessages"
                    total={totalResults}
                    range={{
                        start,
                        end: maxOnPage,
                    }}
                    showAsRange
                    enableTooManyResultsMessage
                    onNavigate={changePage}
                />
            )
        );
    };

    const renderActions = () => <PageHeaderActions right={renderRight()} />;

    const selectedActions = [
        {
            actionName: "read",
            actionIcon: <EnvelopeOpenIcon />,
            tooltip: "mark selected messages as read",
        },
        {
            actionName: "unread",
            actionIcon: <EnvelopeIcon />,
            tooltip: "mark selected messages as unread",
        },
    ];

    // Disable search button if these haven't changed
    const searchDisabled =
        searchCriteria.box === selectedBox.name &&
        currentSearchText === searchCriteria.searchText;

    return (
        <Page
            id={`${baseId}Page`}
            className={classes.listingRoot}
            label="messages"
            isBusy={
                state.loadingMessages ||
                state.settingMessageRead ||
                state.settingMessageUnread
            }
            actions={renderActions()}
            busyType="bar"
        >
            <PageSection>
                <SearchContainer
                    id={baseId}
                    entityName="messages"
                    data={() => state.searchResults.messages}
                    criteria={searchCriteria}
                    facets={state.searchResults.facets}
                    facetConfig={facetConfig}
                    hasResults={state.searchResults.totalResults > 0}
                    onRequestSort={handleRequestSort}
                    getColumns={() => columns}
                    sortDir={searchCriteria.sortDir}
                    sortField={searchCriteria.sortField}
                    totalCount={state.searchResults.totalResults}
                    onSelectedAction={handleSelectedAction}
                    actions={selectedActions}
                    hideCheckbox={
                        searchCriteria.box !== MessageBoxOptionsEnum.Inbox.name
                    }
                    onSearch={handleSearch}
                    searchDisabled={searchDisabled}
                    onSelectAll={actions.selectAllMessages}
                    onDeselectAll={actions.deselectAllMessages}
                    onRowChecked={actions.selectMessage}
                    onRowUnchecked={actions.unselectMessage}
                    selectedRows={state.selectedMessages.map(m => m.toString())}
                    isBusy={state.loadingMessages}
                >
                    <div>
                        <Input
                            autoComplete="off"
                            data-cy="messageListingSearchInput"
                            fullWidth
                            className={classes.keywordInput}
                            value={currentSearchText}
                            autoFocus={true}
                            label="message, name, position, etc"
                            onChange={handleTextSearchEvent}
                            onKeyPress={handleTextSearchKeyPress}
                            inputProps={{
                                id: `${baseId}KeywordInput`,
                            }}
                        />
                        <SelectInput
                            mode="list"
                            options={boxOptions}
                            getOptionLabel={option => option.label}
                            label="mail folder"
                            onChange={handleBoxFilter}
                            value={selectedBox}
                            disableTyping
                        />
                    </div>
                </SearchContainer>
            </PageSection>
        </Page>
    );
};
